import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_URL, PHONE, PHONE_HREF, NAV_LINKS } from "../../mocks/businessData";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <>
      {/* Top utility bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-charcoal text-stone-300 text-xs py-2 hidden md:flex items-center justify-center gap-8">
        <a href={PHONE_HREF} className="flex items-center gap-1.5 hover:text-white transition-colors cursor-pointer">
          <i className="ri-phone-fill text-rust-400"></i>
          <span>{PHONE}</span>
        </a>
        <span className="text-stone-600">|</span>
        <span className="flex items-center gap-1.5">
          <i className="ri-time-fill text-rust-400"></i>
          Mon&ndash;Fri, 8am &ndash; 5pm
        </span>
        <span className="text-stone-600">|</span>
        <span className="flex items-center gap-1.5">
          <i className="ri-map-pin-fill text-rust-400"></i>
          563 Wonderland Rd N, London, Ontario
        </span>
      </div>

      {/* Main nav */}
      <header
        className={`fixed left-0 right-0 z-40 transition-all duration-500 ${
          scrolled
            ? "top-0 md:top-0 bg-white shadow-md"
            : isHome
            ? "top-0 md:top-[34px] bg-transparent"
            : "top-0 md:top-[34px] bg-white shadow-sm"
        }`}
      >
        <div className="w-full px-4 md:px-8 lg:px-16">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0 cursor-pointer">
              <div className="w-36 md:w-44 h-12 md:h-14 flex items-center">
                <img
                  src={LOGO_URL}
                  alt="Upper Canada Landscape Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-sans text-sm font-medium tracking-wide transition-colors duration-200 relative group cursor-pointer ${
                    scrolled || !isHome
                      ? "text-charcoal hover:text-rust-600"
                      : "text-white hover:text-rust-200"
                  }`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-rust-500 group-hover:w-full transition-all duration-300"></span>
                </Link>
              ))}
            </nav>

            {/* CTA area */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href={PHONE_HREF}
                className={`font-sans text-sm font-semibold transition-colors cursor-pointer ${
                  scrolled || !isHome ? "text-rust-600 hover:text-rust-700" : "text-white hover:text-rust-200"
                }`}
              >
                <i className="ri-phone-fill mr-1"></i>
                {PHONE}
              </a>
              <Link
                to="/#contact"
                onClick={() => {
                  setTimeout(() => {
                    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
                className="whitespace-nowrap bg-rust-700 hover:bg-rust-800 text-white font-sans text-sm font-semibold px-6 py-2.5 rounded-sm transition-all duration-200 cursor-pointer"
              >
                Schedule a Consultation
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden p-2 rounded-sm cursor-pointer ${scrolled || !isHome ? "text-charcoal" : "text-white"}`}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-stone-100 shadow-lg">
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="font-sans text-charcoal font-medium py-3 border-b border-stone-100 hover:text-rust-600 transition-colors cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={PHONE_HREF}
                className="mt-3 flex items-center gap-2 bg-stone-50 text-rust-700 font-semibold py-3 px-4 rounded-sm cursor-pointer"
              >
                <i className="ri-phone-fill"></i>
                {PHONE}
              </a>
              <Link
                to="/#contact"
                onClick={() => {
                  setMobileOpen(false);
                  setTimeout(() => {
                    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                  }, 300);
                }}
                className="whitespace-nowrap mt-2 bg-rust-700 hover:bg-rust-800 text-white text-center font-semibold py-3 px-4 rounded-sm transition-colors cursor-pointer"
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
